import { Box, Button, Dialog, IconButton, MenuItem, Select, Typography, Stack, Tooltip, Divider, Fab, DialogContent, InputAdornment, TextField } from "@mui/material";
import { useEffect, useState, useMemo } from "react";
import { GridRowId, GridColDef } from "@mui/x-data-grid";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../redux/reducers";
import { useParams } from "react-router";
import axios from "axios";

// Icons
import SearchIcon from '@mui/icons-material/Search';
import AddCircleIcon from "@mui/icons-material/AddCircle";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance'; // Icône pour Invest

// Logic & Tools
import { IFees } from "../../../interface/fees";
import getForecastMonthList from "../../../tools/forecast/getForecastMonthList";
import { showSnackbar } from "../../../redux/actions";
import { urlAPI } from "../../../config/config";
import getInvestsData from "../../../tools/forecast/getInvestData";
import AddUpdateInvest from "../AddUpdate/AddUpdateInvest";
import { addInvest, handleUpdateInvest } from "../../../tools/forecast/addUpdateInvest";
import { setTotalByMonth } from "../../../redux/actions/forecastActions";

// Components
import AlerteDialog from "../../AlerteDialog/AlerteDialog";
import CustomDataGrid from "../../customDataGrid/CustomDataGrid";

const Invests = ({ forecastVersion, forecastDates }: { forecastVersion: number | undefined, forecastDates: string }) => {
    const dispatch = useDispatch();
    const params = useParams();
    const token = useSelector((state: RootState) => state.user.token);

    const [feesData, setFeesData] = useState<IFees[]>([]);
    const [openModal, setOpenModal] = useState<boolean>(false);
    const [selectedFee, setSelectedFee] = useState<IFees | null>(null);
    const [selectedRows, setSelectedRows] = useState<GridRowId[]>([]);
    const [edit, setEdit] = useState<boolean>(false);
    const [searchLabel, setSearchLabel] = useState<string>("");
    const [agency, setAgency] = useState<string>("All");
    
    const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
    const [actionType, setActionType] = useState<"delete" | "close" | null>(null);

    const monthList = useMemo(() => getForecastMonthList(forecastDates), [forecastDates]);

    useEffect(() => {
        getInvestsData(params.id, forecastVersion, agency, setFeesData, token, dispatch, () => {});
    }, [agency, forecastVersion, openModal]);

    // --- COLONNES ---
    const columns: GridColDef[] = [
        { 
            field: "label", 
            headerName: "Libellé / Bénéficiaire", 
            minWidth: 300, 
            flex: 1,
            renderCell: (params: any) => (
                <Box sx={{ py: 1, lineHeight: '1.2' }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#0f172a" }}>
                        {params.row.label}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#64748b", display: 'block', mt: 0.5 }}>
                        👤 {params.row.beneficiaryName || "Aucun bénéficiaire"}
                    </Typography>
                </Box>
            )
        },
        ...monthList.map((m) => ({
            field: m.field,
            headerName: m.name,
            minWidth: 110,
            align: "right" as const,
            headerAlign: "right" as const,
        }))
    ];

    // --- LIGNES ET TOTAUX ---
    const rows = useMemo(() => {
        // 1. Filtrage (Libellé ou Bénéficiaire)
        const filtered = feesData.filter(f => 
            f.label.toLowerCase().includes(searchLabel.toLowerCase()) ||
            (f.beneficiary?.firstname || "").toLowerCase().includes(searchLabel.toLowerCase()) ||
            (f.beneficiary?.lastname || "").toLowerCase().includes(searchLabel.toLowerCase())
        );

        // 2. Transformation des données pour le DataGrid
        const dataRows = filtered.map((fee) => {
            const monthValues = Object.fromEntries(
                monthList.map(m => {
                    // IMPORTANT : On cherche dans les valeurs de fee.months l'objet qui a le bon label
                    const monthEntry: any = Object.values(fee.months || {}).find(
                        (entry: any) => entry.label === m.field
                    );
                    
                    // Si trouvé on prend .value, sinon on regarde si c'est une valeur directe, sinon 0
                    const val = monthEntry ? (monthEntry.value ?? monthEntry) : 0;
                    return [m.field, `${Number(val).toLocaleString()} €`];
                })
            );

            return { 
                id: fee._id, 
                label: fee.label, 
                beneficiaryName: fee.beneficiary ? `${fee.beneficiary.firstname} ${fee.beneficiary.lastname}` : "",
                ...monthValues 
            };
        });

        // 3. Calcul de la ligne de Total
        const totalRowValues = Object.fromEntries(
            monthList.map(m => {
                const total = filtered.reduce((sum, fee) => {
                    const monthEntry: any = Object.values(fee.months || {}).find(
                        (entry: any) => entry.label === m.field
                    );
                    const val = monthEntry ? (monthEntry.value ?? monthEntry) : 0;
                    return sum + (Number(val) || 0);
                }, 0);
                return [m.field, `${total.toLocaleString()} €`];
            })
        );

        // 4. Mise à jour du store Redux pour les compteurs globaux
        const totalByMonthRedux = Object.fromEntries(
            monthList.map(m => {
                const total = filtered.reduce((sum, fee) => {
                    const monthEntry: any = Object.values(fee.months || {}).find(
                        (entry: any) => entry.label === m.field
                    );
                    const val = monthEntry ? (monthEntry.value ?? monthEntry) : 0;
                    return sum + (Number(val) || 0);
                }, 0);
                return [m.field, total];
            })
        );
        dispatch(setTotalByMonth("Investissements", totalByMonthRedux));

        return [...dataRows, { id: "total", label: "TOTAL GÉNÉRAL", beneficiaryName: "", ...totalRowValues }];
    }, [feesData, searchLabel, monthList, dispatch]);

    // --- ACTIONS ---
    const confirmDelete = async () => {
        try {
            await axios.delete(`${urlAPI}/invest/`, {
                headers: { Authorization: `Bearer ${token}` },
                data: { selectedId: selectedRows },
            });
            setFeesData(prev => prev.filter(f => !selectedRows.includes(f._id)));
            setSelectedRows([]);
            setShowConfirmDialog(false);
            dispatch(showSnackbar("Investissements supprimés", "success"));
        } catch (error) { dispatch(showSnackbar("Erreur lors de la suppression", "error")); }
    };

    const handleDuplication = async () => {
        try {
            const res = await axios.post(`${urlAPI}/invest/duplicate/${selectedRows[0]}`, {}, { headers: { Authorization: `Bearer ${token}` } });
            setFeesData(prev => [...prev, res.data.data]);
            setSelectedRows([]);
            dispatch(showSnackbar("Investissement dupliqué", "success"));
        } catch (error) { dispatch(showSnackbar("Erreur duplication", "error")); }
    };

    return (
        <Box sx={{ p: 3, bgcolor: "#f8fafc", minHeight: "100vh" }}>
            
            {/* --- HEADER --- */}
            <Box sx={{ bgcolor: "white", p: 3, borderRadius: 4, mb: 4, border: "1px solid #e2e8f0", boxShadow: "0px 4px 20px rgba(0,0,0,0.03)" }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <AccountBalanceIcon sx={{ color: "#0f172a" }} />
                        <Typography variant="h5" sx={{ fontWeight: 800, color: "#0f172a" }}>Investissements</Typography>
                    </Stack>
                    <Typography variant="caption" sx={{ fontWeight: 800, color: "#64748b", bgcolor: "#f1f5f9", px: 1.5, py: 0.5, borderRadius: 1 }}>V{forecastVersion}</Typography>
                </Box>

                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
                    <Select value={agency} onChange={(e) => setAgency(e.target.value)} size="small" sx={{ minWidth: 160, borderRadius: 2, bgcolor: "#f1f5f9", "& fieldset": { border: "none" }, fontWeight: 600 }}>
                        <MenuItem value="All">Toutes les agences</MenuItem>
                        <MenuItem value="Nord">Agence Nord</MenuItem>
                        <MenuItem value="Sud">Agence Sud</MenuItem>
                    </Select>

                    <TextField 
                        size="small" placeholder="Rechercher un investissement..." value={searchLabel} onChange={(e) => setSearchLabel(e.target.value)}
                        sx={{ bgcolor: "#f1f5f9", borderRadius: 2, "& fieldset": { border: "none" }, minWidth: 280 }}
                        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" color="disabled"/></InputAdornment> }}
                    />

                    <Box sx={{ flexGrow: 1 }} />
                    <Button variant="contained" startIcon={<AddCircleIcon />} onClick={() => { setEdit(false); setSelectedFee(null); setOpenModal(true); }} sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700, px: 3 }}>
                        Ajouter un investissement
                    </Button>
                </Box>
            </Box>

            {/* --- TABLEAU --- */}
            <Box sx={{ mb: 12 }}>
                <CustomDataGrid
                    rows={rows}
                    columns={columns}
                    getRowHeight={() => "auto"}
                    checkboxSelection
                    rowSelectionModel={selectedRows}
                    onRowSelectionModelChange={(newSelection) => setSelectedRows(newSelection)}
                    isRowSelectable={(params: any) => params.row.id !== 'total'}
                    onCellClick={(params: any) => {
                        if (params.row.id !== 'total' && params.field !== "__check__") {
                            const rowData = feesData.find(f => f._id === params.row.id);
                            if (rowData) { setEdit(true); setSelectedFee(rowData); setOpenModal(true); }
                        }
                    }}
                    sx={{
                        '& .MuiDataGrid-row[data-id="total"]': {
                            bgcolor: '#f1f5f9 !important',
                            fontWeight: '800 !important',
                            pointerEvents: 'none',
                            '& .MuiDataGrid-cell': { color: '#0f172a', borderTop: '2px solid #e2e8f0' },
                            '& .MuiDataGrid-checkboxInput': { display: 'none' }
                        }
                    }}
                />
            </Box>

            {/* --- DOCK FLOTTANT --- */}
            <Box sx={{ position: "fixed", bottom: 30, right: "50%", transform: "translateX(50%)", display: "flex", alignItems: "center", gap: 2, bgcolor: "white", p: "12px 24px", borderRadius: "100px", border: "1px solid #e2e8f0", boxShadow: "0px 10px 40px rgba(0,0,0,0.1)", zIndex: 1100 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, mr: 1 }}>{selectedRows.length} sélectionnés</Typography>
                <Tooltip title="Supprimer"><Fab color="error" size="small" disabled={selectedRows.length === 0} onClick={() => { setActionType("delete"); setShowConfirmDialog(true); }}><DeleteIcon fontSize="small" /></Fab></Tooltip>
                <Tooltip title="Dupliquer"><Fab color="info" size="small" disabled={selectedRows.length !== 1} onClick={handleDuplication} sx={{ color: 'white' }}><ContentCopyIcon fontSize="small" /></Fab></Tooltip>
                <Divider orientation="vertical" flexItem />
                <Fab color="primary" size="medium" onClick={() => { setEdit(false); setSelectedFee(null); setOpenModal(true); }}><AddCircleIcon /></Fab>
            </Box>

            {/* MODALE */}
            <Dialog open={openModal} fullWidth maxWidth="xl" PaperProps={{ sx: { borderRadius: 4 } }}>
                <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #eee' }}>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>{edit ? "Modifier" : "Ajouter"} un investissement</Typography>
                    <IconButton onClick={() => { setActionType("close"); setShowConfirmDialog(true); }}><CloseIcon /></IconButton>
                </Box>
                <DialogContent sx={{ p: 0 }}>
                    <AddUpdateInvest handleSubmit={edit ? handleUpdateInvest : addInvest} forecastId={params.id} version={forecastVersion} setOpenModal={setOpenModal} monthList={monthList} initialValue={selectedFee || {}} isEdit={edit} />
                </DialogContent>
            </Dialog>

            <AlerteDialog 
                open={showConfirmDialog} 
                handleClose={() => setShowConfirmDialog(false)} 
                deleteProject={actionType === "delete" ? confirmDelete : () => { setShowConfirmDialog(false); setOpenModal(false); }} 
                title={actionType === "delete" ? "Supprimer ?" : "Abandonner ?"} 
                contentText={actionType === "delete" ? `Voulez-vous supprimer ces ${selectedRows.length} investissements ?` : "Voulez-vous quitter sans enregistrer ?"} 
                color="error" 
            />
        </Box>
    );
};

export default Invests;